import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DialogBox from "@/components/Common/DialogBox";
import Loader from "@/components/Loader";
import MarketingCampaignItemCard from "@/components/marketing/MarketingCampaignItemCard";
import { marketingCampaignsService } from "@/services/marketingCampaignsService";
import { emailService } from "@/services/emailService";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useDebounce } from "@/utils/helpers";

const CHANNEL_LABELS = {
  email: "Email",
  sms: "SMS",
  both: "Email & SMS",
};

const isEmptyHtml = (html = "") =>
  !html.trim() || html.trim() === "<p><br></p>";

const toPlainText = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeCampaign = (item) => {
  const candidates = Array.isArray(item.candidates) ? item.candidates : [];
  const candidateIds = Array.isArray(item.candidate_ids)
    ? item.candidate_ids
    : candidates.map((c) => c.user_id ?? c.id).filter(Boolean);

  return {
    id: item.id,
    name: item.name ?? "",
    template_id: item.template_id ?? item.template?.id ?? null,
    template_name:
      item.template_name ?? item.template?.name ?? item.template_title ?? "",
    channel:
      item.channel ?? item.template?.channel ?? item.template_channel ?? "email",
    subject: item.subject ?? item.template?.subject ?? "",
    email_body:
      item.email_body ?? item.body ?? item.template?.body ?? item.content ?? "",
    sms_body: item.sms_body ?? item.template?.sms_body ?? "",
    candidate_ids: candidateIds,
    candidates,
    candidates_count:
      item.candidates_count ??
      item.candidate_count ??
      candidateIds.length ??
      0,
    created_at: item.created_at,
    updated_at: item.updated_at,
  };
};

const MarketingCampaignsTab = () => {
  const navigate = useNavigate();
  const { showToast } = useToastStore;

  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewCampaign, setViewCampaign] = useState(null);
  const [campaignToSend, setCampaignToSend] = useState(null);
  const [sendChannel, setSendChannel] = useState("email");
  const [showSendModal, setShowSendModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const isSending = isSendingEmail || isSendingSms;

  const loadCampaigns = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = { per_page: 100 };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await marketingCampaignsService.getCampaigns(params);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.campaigns)
          ? response.campaigns
          : Array.isArray(response)
            ? response
            : [];

      setCampaigns(list.map(normalizeCampaign));
    } catch (error) {
      showToast({
        message: error.message || "Failed to fetch campaigns",
        type: "error",
      });
      setCampaigns([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, showToast]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const updateSearchTerm = (term) => {
    setSearchTerm(term);
  };

  const debouncedSearch = useDebounce(updateSearchTerm, 500);

  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  const handleOpenCreate = () => {
    navigate("/marketing/campaigns/create");
  };

  const handleOpenEdit = (campaign) => {
    navigate(`/marketing/campaigns/${campaign.id}/edit`);
  };

  const handleOpenView = (campaign) => {
    setViewCampaign(campaign);
    setShowViewModal(true);
  };

  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const getCampaignCandidateIds = async (campaign) => {
    if (Array.isArray(campaign.candidate_ids) && campaign.candidate_ids.length) {
      return campaign.candidate_ids;
    }

    const response = await marketingCampaignsService.getCampaign(campaign.id);
    const detail = response?.data ?? response;
    const candidates = Array.isArray(detail?.candidates) ? detail.candidates : [];
    const candidateIds = Array.isArray(detail?.candidate_ids)
      ? detail.candidate_ids
      : candidates.map((c) => c.user_id ?? c.id).filter(Boolean);

    return candidateIds;
  };

  const handleSendEmailClick = (campaign) => {
    if (!campaign.subject?.trim()) {
      showToast({
        message: "Campaign has no email subject",
        type: "error",
      });
      return;
    }

    if (isEmptyHtml(campaign.email_body)) {
      showToast({
        message: "Campaign has no email content",
        type: "error",
      });
      return;
    }

    setSendChannel("email");
    setCampaignToSend(campaign);
    setShowSendModal(true);
  };

  const handleSendSmsClick = (campaign) => {
    if (isEmptyHtml(campaign.sms_body)) {
      showToast({
        message: "Campaign has no SMS content",
        type: "error",
      });
      return;
    }

    const smsBody = toPlainText(campaign.sms_body);
    if (smsBody.length > 1600) {
      showToast({
        message: "SMS message must be 1600 characters or fewer",
        type: "error",
      });
      return;
    }

    setSendChannel("sms");
    setCampaignToSend(campaign);
    setShowSendModal(true);
  };

  const handleSendEmail = async () => {
    if (!campaignToSend) return;

    try {
      setIsSendingEmail(true);
      const candidate_ids = await getCampaignCandidateIds(campaignToSend);

      if (!candidate_ids.length) {
        showToast({
          message: "This campaign has no candidates",
          type: "error",
        });
        return;
      }

      const result = await emailService.sendNewsletter({
        channel: "email",
        candidates: candidate_ids.map((id) => ({ id })),
        subject: campaignToSend.subject.trim(),
        body: campaignToSend.email_body.trim(),
        template_id: campaignToSend.template_id || undefined,
        campaign_id: campaignToSend.id,
      });

      const sentCount = result?.queued ?? candidate_ids.length;
      showToast({
        message: `Email queued for ${sentCount} contact${sentCount === 1 ? "" : "s"}!`,
        type: "success",
      });

      setShowSendModal(false);
      setCampaignToSend(null);
    } catch (error) {
      showToast({
        message: error.message || "Failed to send email",
        type: "error",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendSms = async () => {
    if (!campaignToSend) return;

    try {
      setIsSendingSms(true);
      const candidate_ids = await getCampaignCandidateIds(campaignToSend);

      if (!candidate_ids.length) {
        showToast({
          message: "This campaign has no candidates",
          type: "error",
        });
        return;
      }

      const result = await emailService.sendNewsletter({
        channel: "sms",
        candidates: candidate_ids.map((id) => ({ id })),
        sms_body: toPlainText(campaignToSend.sms_body),
        template_id: campaignToSend.template_id || undefined,
        campaign_id: campaignToSend.id,
      });

      const sentCount = result?.queued ?? candidate_ids.length;
      showToast({
        message: `SMS queued for ${sentCount} contact${sentCount === 1 ? "" : "s"}!`,
        type: "success",
      });

      setShowSendModal(false);
      setCampaignToSend(null);
    } catch (error) {
      showToast({
        message: error.message || "Failed to send SMS",
        type: "error",
      });
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleDelete = async () => {
    if (!campaignToDelete) return;

    try {
      setIsDeleting(true);
      await marketingCampaignsService.deleteCampaign(campaignToDelete.id);
      showToast({ message: "Campaign deleted successfully", type: "success" });
      setShowDeleteModal(false);
      setCampaignToDelete(null);
      await loadCampaigns();
    } catch (error) {
      showToast({
        message: error.message || "Failed to delete campaign",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex w-full flex-wrap items-center gap-3">
        <div className="w-full sm:max-w-[384px] shrink-0">
          <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden w-full">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search campaigns"
              value={tempSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
            />
          </div>
        </div>

        <div className="ml-auto flex shrink-0 items-center">
          <button
            type="button"
            onClick={handleOpenCreate}
            className="bg-primary-dark text-white px-4 h-11 rounded-lg shadow-sm font-semibold transition-all hover:bg-slate-900 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Campaign
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader bgColor="bg-transparent" loadingText="Loading campaigns..." />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <h3 className="text-lg font-medium text-foreground">
            No Campaigns Found
          </h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {campaigns.map((campaign) => (
            <MarketingCampaignItemCard
              key={campaign.id}
              campaign={campaign}
              onView={handleOpenView}
              onEdit={handleOpenEdit}
              onDelete={handleDeleteClick}
              onSendEmail={handleSendEmailClick}
              onSendSms={handleSendSmsClick}
              isSendingEmail={
                isSendingEmail && campaignToSend?.id === campaign.id
              }
              isSendingSms={isSendingSms && campaignToSend?.id === campaign.id}
            />
          ))}
        </div>
      )}

      <DialogBox
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewCampaign(null);
        }}
        title="Campaign Details"
        className="w-[45%] max-md:w-[90%]"
        showIcon={false}
      >
        <div>
          <div className="py-3 space-y-1">
            <p className="text-lg font-semibold text-primary-dark break-words leading-7">
              {viewCampaign?.name || "-"}
            </p>
            <p className="text-sm text-muted-foreground">
              Channel:{" "}
              {CHANNEL_LABELS[viewCampaign?.channel] ||
                viewCampaign?.channel ||
                "-"}
            </p>
            {viewCampaign?.template_name && (
              <p className="text-sm text-muted-foreground">
                Template: {viewCampaign.template_name}
              </p>
            )}
            {viewCampaign?.channel !== "sms" && viewCampaign?.subject && (
              <p className="text-sm text-muted-foreground break-words">
                Subject: {viewCampaign.subject}
              </p>
            )}
            <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              {viewCampaign?.candidates_count ?? 0} candidate
              {(viewCampaign?.candidates_count ?? 0) === 1 ? "" : "s"}
            </p>
          </div>

          {viewCampaign?.email_body && (
            <div className="rounded-xl border border-border bg-muted px-4 py-4 mb-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Email</p>
              <div className="max-h-[30vh] overflow-y-auto pr-1 prose prose-sm max-w-none">
                <div
                  dangerouslySetInnerHTML={{ __html: viewCampaign.email_body }}
                />
              </div>
            </div>
          )}

          {viewCampaign?.sms_body && (
            <div className="rounded-xl border border-border bg-muted px-4 py-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">SMS</p>
              <div className="max-h-[30vh] overflow-y-auto pr-1 prose prose-sm max-w-none">
                <div
                  dangerouslySetInnerHTML={{ __html: viewCampaign.sms_body }}
                />
              </div>
            </div>
          )}
        </div>
      </DialogBox>

      <DialogBox
        isOpen={showSendModal}
        onClose={() => {
          if (isSending) return;
          setShowSendModal(false);
          setCampaignToSend(null);
        }}
        title={
          sendChannel === "sms" ? "Send Campaign SMS" : "Send Campaign Email"
        }
        className="w-[40%] max-md:w-[90%]"
        actionButton={
          <button
            type="button"
            onClick={sendChannel === "sms" ? handleSendSms : handleSendEmail}
            disabled={isSending}
            className="inline-flex w-full justify-center rounded-md bg-primary-dark px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-slate-900 sm:ml-3 sm:w-auto disabled:opacity-50"
          >
            {isSending
              ? "Sending..."
              : sendChannel === "sms"
                ? "Send SMS"
                : "Send Email"}
          </button>
        }
      >
        <p className="text-base text-muted-foreground">
          Send &quot;{campaignToSend?.name}&quot; to this campaign&apos;s
          selected candidates?
        </p>
      </DialogBox>

      <DialogBox
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCampaignToDelete(null);
        }}
        title="Delete Campaign"
        className="w-[40%] max-md:w-[90%]"
        actionButton={
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto disabled:opacity-50"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        }
      >
        <p className="text-base text-muted-foreground">
          Are you sure you want to delete &quot;{campaignToDelete?.name}&quot;?
          This action cannot be undone.
        </p>
      </DialogBox>
    </div>
  );
};

export default MarketingCampaignsTab;
